/**
 * Formato do VALOR de uma regra de filtro multi-seleção — fonte de verdade única.
 *
 * Histórico: o valor era sempre CSV (`"a,b,c"`). Só que opção de campo `select` pode
 * CONTER vírgula no próprio texto ("Sim, auxílio doença", "Não, nenhum benefício") —
 * em produção são 16 opções assim, em 8 campos de 3 orgs. O CSV quebrava a opção em
 * dois tokens: a UI mostrava "2 selecionados" com as caixas desmarcadas e o filtro
 * nunca casava, silenciosamente.
 *
 * Formato novo: tokens separados por U+001F (unit separator), impossível de aparecer
 * em texto de formulário, COM o separador também no início do valor — sem isso um
 * token único que contém vírgula ("Sim, auxílio doença") seria indistinguível de CSV.
 *
 * Compatibilidade: `splitFilterValue` aceita os dois formatos e `joinFilterValue` só
 * emite o novo quando algum token tem vírgula. Ou seja, a esmagadora maioria dos
 * valores continua CSV e todo filtro já salvo é lido exatamente como antes.
 *
 * Espelha `public._filter_split_values` no Postgres — o mesmo filtro avaliado aqui,
 * pelo push-down PostgREST ou pelo construtor SQL tem que devolver o MESMO conjunto.
 */
export const FILTER_VALUE_SEPARATOR = "\u001F";

/** Divide o valor de uma regra em tokens, aceitando o formato novo e o CSV legado. */
export function splitFilterValue(raw: string | null | undefined): string[] {
  const s = raw == null ? "" : String(raw);
  if (!s) return [];
  const sep = s.includes(FILTER_VALUE_SEPARATOR) ? FILTER_VALUE_SEPARATOR : ",";
  return s.split(sep).map((p) => p.trim()).filter(Boolean);
}

/**
 * Serializa tokens no formato mais conservador possível: CSV quando nenhum token tem
 * vírgula (compatível com qualquer consumidor antigo), unit separator quando tem.
 */
export function joinFilterValue(tokens: string[]): string {
  const clean = tokens
    .map((t) => t.split(FILTER_VALUE_SEPARATOR).join("").trim())
    .filter(Boolean);
  if (clean.length === 0) return "";
  if (!clean.some((t) => t.includes(","))) return clean.join(",");
  return FILTER_VALUE_SEPARATOR + clean.join(FILTER_VALUE_SEPARATOR);
}

/** `true` quando o valor tem mais de um token (em qualquer um dos dois formatos). */
export function isMultiToken(raw: string | null | undefined): boolean {
  return splitFilterValue(raw).length > 1;
}

/**
 * Cita um token para uso dentro de `in.(...)` do PostgREST.
 *
 * O `.in()` do postgrest-js já faz isso, mas as chamadas manuais de `NOT IN` montam a
 * lista à mão (`(${parts.join(",")})`) — sem citação, um token com vírgula ou
 * parêntese vira vários valores ou quebra a query. Há 11 opções com parêntese em
 * produção (ex.: "Faz tratamento regularmente (terapia, médico e etc)").
 *
 * Ref.: https://postgrest.org/en/stable/references/api/tables_views.html#reserved-characters
 */
export function quotePostgrestInToken(token: string): string {
  if (!/[,()."\\]/.test(token)) return token;
  return `"${token.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
}

/** Monta o corpo de um `in.(...)` do PostgREST com todos os tokens citados. */
export function postgrestInList(tokens: string[]): string {
  return tokens.map(quotePostgrestInToken).join(",");
}
